import type { PoiIconSizingRule } from '../types/map';

export const POI_ICON_ZOOM_MIN = 0;
export const POI_ICON_ZOOM_MAX = 22;
export const POI_BASE_ICON_SIZE_PX = 64;

export const DEFAULT_POI_ICON_SIZING_RULES: PoiIconSizingRule[] = [
  { zoomFrom: 0, zoomTo: 7, svgSizePx: 14 },
  { zoomFrom: 8, zoomTo: 13, svgSizePx: 18 },
  { zoomFrom: 14, zoomTo: 17, svgSizePx: 22 },
  { zoomFrom: 18, zoomTo: 22, svgSizePx: 26 },
];

export type PoiIconSizingValidationResult =
  | { ok: true; rules: PoiIconSizingRule[] }
  | { ok: false; error: string };

export function normalizePoiIconSizingRules(rules: PoiIconSizingRule[]): PoiIconSizingRule[] {
  return [...rules].sort((a, b) => a.zoomFrom - b.zoomFrom);
}

export function validatePoiIconSizingRules(rules: PoiIconSizingRule[]): PoiIconSizingValidationResult {
  if (!Array.isArray(rules) || rules.length === 0) {
    return { ok: false, error: 'At least one zoom rule is required.' };
  }

  const sorted = normalizePoiIconSizingRules(rules);
  if (sorted[0].zoomFrom !== POI_ICON_ZOOM_MIN) {
    return { ok: false, error: `Rules must start at zoom ${POI_ICON_ZOOM_MIN}.` };
  }
  if (sorted[sorted.length - 1].zoomTo !== POI_ICON_ZOOM_MAX) {
    return { ok: false, error: `Rules must end at zoom ${POI_ICON_ZOOM_MAX}.` };
  }

  for (let i = 0; i < sorted.length; i += 1) {
    const rule = sorted[i];
    if (!Number.isInteger(rule.zoomFrom) || !Number.isInteger(rule.zoomTo) || !Number.isInteger(rule.svgSizePx)) {
      return { ok: false, error: `Rule ${i + 1} must use integer values.` };
    }
    if (rule.zoomFrom < POI_ICON_ZOOM_MIN || rule.zoomTo > POI_ICON_ZOOM_MAX || rule.zoomFrom > rule.zoomTo) {
      return { ok: false, error: `Rule ${i + 1} has an invalid zoom range.` };
    }
    if (rule.svgSizePx < 4 || rule.svgSizePx > 256) {
      return { ok: false, error: `Rule ${i + 1} must use an SVG size between 4 and 256px.` };
    }
    if (i > 0) {
      const prev = sorted[i - 1];
      if (rule.zoomFrom !== prev.zoomTo + 1) {
        return { ok: false, error: 'Rules must be contiguous with no gaps or overlaps.' };
      }
    }
  }

  return { ok: true, rules: sorted };
}

export function coercePoiIconSizingRules(input: unknown): PoiIconSizingRule[] {
  if (!Array.isArray(input)) return DEFAULT_POI_ICON_SIZING_RULES;

  const mapped = input
    .map((rule) => {
      const value = rule as Record<string, unknown>;
      const zoomFrom = Number(value.zoomFrom);
      const zoomTo = Number(value.zoomTo);
      const svgSizePx = Number(value.svgSizePx);
      return {
        id: typeof value.id === 'string' ? value.id : undefined,
        zoomFrom,
        zoomTo,
        svgSizePx,
      };
    })
    .filter((rule) => Number.isFinite(rule.zoomFrom) && Number.isFinite(rule.zoomTo) && Number.isFinite(rule.svgSizePx));

  const validation = validatePoiIconSizingRules(mapped);
  return validation.ok ? validation.rules : DEFAULT_POI_ICON_SIZING_RULES;
}

export function buildPoiIconSizeExpression(
  rules: PoiIconSizingRule[],
  baseIconSizePx: number = POI_BASE_ICON_SIZE_PX
): any[] {
  const validation = validatePoiIconSizingRules(rules);
  const safeRules = validation.ok ? validation.rules : DEFAULT_POI_ICON_SIZING_RULES;

  const expression: any[] = ['step', ['zoom'], safeRules[0].svgSizePx / baseIconSizePx];
  for (let i = 1; i < safeRules.length; i += 1) {
    const rule = safeRules[i];
    expression.push(rule.zoomFrom, rule.svgSizePx / baseIconSizePx);
  }
  return expression;
}

export function getPoiOutlineStrokeWidth(baseIconSizePx: number = POI_BASE_ICON_SIZE_PX): number {
  return Math.max(1.75, Math.min(6, baseIconSizePx * 0.1));
}
